'use client';

/**
 * Organization provider component
 * Manages organization state and provides it to child components
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import OrganizationContext, {
  OrganizationContextProps,
} from '@src/v3/contexts/organizationContext';
import {
  Organization,
  OrganizationMembership,
  PendingInvite,
} from '@src/v3/interfaces/organization';
import {
  getMyOrganization,
  getPendingInvites,
  acceptInvite as acceptInviteApi,
  rejectInvite as rejectInviteApi,
} from '@src/v3/services/organization';
import { getUserType, isLoggedIn } from '@src/v3/utils/localStorage';
import OrganizationInviteModal from '@src/v3/components/organizationInviteModal';
import useProfile from '@src/v3/hooks/useProfile';
import { usePathname } from 'next/navigation';
import { APP_ROUTE } from '@src/v3/constants/routes';

interface OrganizationProviderProps {
  children: React.ReactNode;
}

const INVITE_CHECK_INTERVAL = 60000; // Check for new invites every minute

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({
  children,
}) => {
  const { isUserLoggedIn } = useProfile();
  const pathname = usePathname();
  const [hasMembership, setHasMembership] = useState(false);
  const [membership, setMembership] = useState<OrganizationMembership | null>(
    null
  );
  const [organization, setOrganization] = useState<Organization | null>(null);
  // Start as true so layout waits for initial fetch before checking permissions
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [hasPendingInvites, setHasPendingInvites] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);

  /**
   * Fetch the current user's organization membership
   */
  const fetchMyOrganization = useCallback(async () => {
    if (!isLoggedIn() || getUserType().toUpperCase() !== 'AGENT') {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getMyOrganization();
      setHasMembership(response.has_membership);
      setMembership(response.membership);
      setOrganization(response.organization);
    } catch (err: unknown) {
      console.error('Failed to fetch organization:', err);
      const error = err as { message?: string };
      setError(error?.message || 'Failed to fetch organization');
      setHasMembership(false);
      setMembership(null);
      setOrganization(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch pending organization invites
   */
  const fetchPendingInvites = useCallback(async () => {
    if (!isLoggedIn() || getUserType().toUpperCase() !== 'AGENT') {
      return;
    }

    try {
      const response = await getPendingInvites();
      setHasPendingInvites(response.has_pending_invites);
      setPendingInvites(response.invites);
    } catch (err) {
      console.error('Failed to fetch pending invites:', err);
      setHasPendingInvites(false);
      setPendingInvites([]);
    }
  }, []);

  /**
   * Accept an organization invite
   */
  const acceptInvite = useCallback(
    async (token: string): Promise<{ success: boolean; error?: string }> => {
      try {
        const newMembership = await acceptInviteApi(token);
        setMembership(newMembership);
        setHasMembership(true);

        // Refresh to get full organization data
        await fetchMyOrganization();

        // Clear pending invites since accepting one rejects others
        setHasPendingInvites(false);
        setPendingInvites([]);

        return { success: true };
      } catch (err: unknown) {
        console.error('Failed to accept invite:', err);
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to accept invite';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [fetchMyOrganization]
  );

  /**
   * Reject an organization invite
   */
  const rejectInvite = useCallback(
    async (token: string): Promise<{ success: boolean; error?: string }> => {
      try {
        await rejectInviteApi(token);

        // Remove from pending invites
        setPendingInvites((prev) =>
          prev.filter((invite) => invite.invite_token !== token)
        );
        const remaining = pendingInvites.filter(
          (i) => i.invite_token !== token
        );
        setHasPendingInvites(remaining.length > 0);

        return { success: true };
      } catch (err: unknown) {
        console.error('Failed to reject invite:', err);
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to reject invite';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [pendingInvites]
  );

  /**
   * Refresh organization data
   */
  const refreshOrganization = useCallback(async () => {
    await fetchMyOrganization();
    await fetchPendingInvites();
  }, [fetchMyOrganization, fetchPendingInvites]);

  // Fetch organization on mount if user is an agent
  useEffect(() => {
    if (isLoggedIn() && getUserType().toUpperCase() === 'AGENT') {
      fetchMyOrganization();
      fetchPendingInvites();
    } else {
      // Not an agent or not logged in - no need to wait for fetch
      setIsLoading(false);
    }
  }, [fetchMyOrganization, fetchPendingInvites]);

  // Re-check org state immediately after auth changes so admin access/invite modal
  // updates without waiting for periodic polling.
  useEffect(() => {
    if (!isUserLoggedIn || getUserType().toUpperCase() !== 'AGENT') {
      setHasMembership(false);
      setMembership(null);
      setOrganization(null);
      setHasPendingInvites(false);
      setPendingInvites([]);
      setIsLoading(false);
      return;
    }

    refreshOrganization();
  }, [isUserLoggedIn, refreshOrganization]);

  // Periodically check for new invites (similar to has_unreads pattern)
  useEffect(() => {
    if (!isLoggedIn() || getUserType().toUpperCase() !== 'AGENT') {
      return undefined;
    }

    const intervalId = setInterval(() => {
      // Only check if not already in an org
      if (!hasMembership) {
        fetchPendingInvites();
      }
    }, INVITE_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hasMembership, fetchPendingInvites]);

  const contextValue: OrganizationContextProps = useMemo(
    () => ({
      hasMembership,
      membership,
      organization,
      isLoading,
      error,
      hasPendingInvites,
      pendingInvites,
      fetchMyOrganization,
      fetchPendingInvites,
      acceptInvite,
      rejectInvite,
      refreshOrganization,
    }),
    [
      hasMembership,
      membership,
      organization,
      isLoading,
      error,
      hasPendingInvites,
      pendingInvites,
      fetchMyOrganization,
      fetchPendingInvites,
      acceptInvite,
      rejectInvite,
      refreshOrganization,
    ]
  );

  const isSubscriptionFlow =
    pathname === APP_ROUTE.SUBSCRIPTION ||
    pathname === APP_ROUTE.VERIFICATION_REQUIRED ||
    pathname.startsWith(`${APP_ROUTE.SUBSCRIPTION}/`);

  // Show invite modal if user has pending invites and is not already in an org
  const shouldShowInviteModal =
    !isLoading &&
    isLoggedIn() &&
    getUserType().toUpperCase() === 'AGENT' &&
    !isSubscriptionFlow &&
    pendingInvites.length > 0 &&
    (!hasMembership || membership?.status !== 'JOINED');

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
      {shouldShowInviteModal && (
        <OrganizationInviteModal
          invites={pendingInvites}
          onAccept={acceptInvite}
          onReject={rejectInvite}
        />
      )}
    </OrganizationContext.Provider>
  );
};

export default OrganizationProvider;
